/// <amd-module name="TFS/Samples/Charts/ChartsSample" />
import * as React from "react";

import { Fabric } from "OfficeFabric/Fabric";
import * as Resources from "TFS/Samples/Charts/Resources";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { ComboBox, IComboBoxOption } from "VSSUI/ComboBox";
import { FormItem } from "VSSUI/FormItem";
import { Header, TitleSize } from "VSSUI/Header";
import { Page } from "VSSUI/Page";
import { TextField } from "VSSUI/TextField";

import {
    ChartOptionsFactories,
    ChartSDKWidgetSettings,
    ConfigMode,
    ConfigModeMetadata,
    SDKWidgetContentProvider
} from "TFS/Samples/Charts/ChartsSampleGenerator";
import { Chart } from "VSS/Features/Charts/Chart";
import { ErrorBoundary } from "VSS/Platform/Components/ErrorBoundary";

interface IChartsSampleProps {}

interface IChartsSampleState {
    selectedItemKey: ConfigMode;
    configurationText: string;
}

/**
 * This sample provides a limited gallery experience to review available charts/snippets, ported to new platform from the Chart SDK widget.
 */
class ChartsSample extends VssComponent<IChartsSampleProps, IChartsSampleState> {
    private loggedPerfEvent: boolean;

    constructor(props: IChartsSampleProps, context: IVssComponentContext) {
        super(props, context);

        const initialChartKey = ConfigMode.CommonChart_CFD;
        this.state = this.generateChartState(initialChartKey);
    }

    public componentDidUpdate(prevProps: IChartsSampleProps, prevState: IChartsSampleState): void {
        super.componentDidUpdate(prevProps, prevState);

        if (!this.loggedPerfEvent) {
            const perfService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
            perfService.markTimeToInteractive("tfs", "WebPlatform.Samples.ChartsSample");
            this.loggedPerfEvent = true;
        }
    }

    public render(): JSX.Element {
        const items = SDKWidgetContentProvider.getModeMetadata();
        const chartMetadata: ConfigModeMetadata =
            this.state.selectedItemKey == null ? items[0] : SDKWidgetContentProvider.findModeMetadata(this.state.selectedItemKey);

        const chartOptions = ChartOptionsFactories.applyHostOptions(ChartSDKWidgetSettings.parseOrDefault(this.state.configurationText), true);

        // Set page context in chartOptions so it can act as a service provider.
        chartOptions.pageContext = this.context.pageContext;

        return (
            <Fabric className="flex-grow flex-row">
                <Page className="flex-grow charts-sample-container">
                    <Header className="flex-noshrink" title={Resources.Title} titleSize={TitleSize.Medium} />
                    <div className="page-content">
                        <div>{Resources.Description}</div>
                        <ComboBox
                            options={this.getChartModesAsComboItems(items)}
                            onChanged={(value: IComboBoxOption) => {
                                this.setState(this.generateChartState(value.key as ConfigMode));
                            }}
                            selectedKey={chartMetadata.value}
                        />
                        <ErrorBoundary handlerComponentType="tfs.page-error-handler">
                            <Chart {...chartOptions} />
                        </ErrorBoundary>
                        <FormItem label={Resources.EditableChartSettings}>
                            <TextField multiline onChange={this.updateState} rows={20} value={this.state.configurationText} />
                        </FormItem>
                        <ErrorBoundary handlerComponentType="tfs.page-error-handler">
                            <span>Screen reader accessible html Table representation of Chart Data</span>
                            <Chart {...chartOptions} showAccessibleForm={true} />
                        </ErrorBoundary>
                    </div>
                </Page>
            </Fabric>
        );
    }

    private getChartModesAsComboItems(items: ConfigModeMetadata[]): IComboBoxOption[] {
        return items.map(item => {
            const option: IComboBoxOption = {
                text: item.name,
                key: item.value
            };
            return option;
        });
    }

    private generateChartState(configMode: ConfigMode) {
        return {
            selectedItemKey: configMode,
            configurationText: ChartSDKWidgetSettings.stringifyForEdit(
                SDKWidgetContentProvider.findModeMetadata(configMode).optionFactory(this.context.pageContext)
            )
        };
    }

    private updateState = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        return this.setState({ configurationText: event.currentTarget.value, selectedItemKey: this.state.selectedItemKey });
    };
}

Components.add("charts-sample-view", ChartsSample);
